/*
 * The week overview's rules, apart from the page that draws them.
 *
 * Pure, in the shape of workOrderPriorityCounts.js and workOrderSearch.js, so which week an order
 * lands in can be tested without rendering a week.
 *
 * Bucketed in the client: the list endpoint already returns every order with the dates this needs,
 * and a per-week endpoint would be a second answer to keep in step with the grid's.
 */
import {
  addDays,
  addWeeks,
  differenceInCalendarDays,
  getISOWeek,
  getISOWeekYear,
  isBefore,
  isSameDay,
  set,
  startOfDay,
  startOfISOWeek,
} from "date-fns";
import { asUtcIso } from "../cmms/cmmsFormat";
import { WorkOrderStatus, WorkOrderType, isWorkOrderOpen } from "./workOrderEnums";

export const WeekGroupBy = {
  Department: "department",
  Trade: "trade",
};

const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const date = new Date(asUtcIso(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

/*
 * Monday 00:00 of the ISO week the value falls in, in the browser's own time.
 *
 * Local rather than UTC on purpose: a breakdown reported at 00:30 on a Swedish Monday is Monday's
 * work, even though it is still Sunday in UTC.
 */
export const getWeekStart = (value) => startOfISOWeek(toDate(value) ?? new Date());

/*
 * What the navigator prints. The ISO week-year, not the calendar year, so the week that starts on
 * 30 December 2024 reads "week 1, 2025" the way a Swedish wall calendar does.
 */
export const getWeekInfo = (weekStart) => {
  const start = startOfISOWeek(weekStart);
  return {
    weekNumber: getISOWeek(start),
    weekYear: getISOWeekYear(start),
    start,
    end: addDays(start, 6),
  };
};

/*
 * The date that decides an order's week: when it is planned to start, and failing that, when it
 * was raised.
 *
 * The fallback is what places a breakdown, which nobody plans, and a sweep-generated preventive
 * order, which the sweep raises on its due day without setting a planned date.
 */
export const getPlacementDate = (workOrder) =>
  toDate(workOrder?.plannedStartDate) ?? toDate(workOrder?.createdAt);

/*
 * Akut is the work that arrived, planerad the work that was scheduled. By type rather than by
 * priority: a low-priority breakdown is still something nobody planned for.
 */
const acuteTypes = [WorkOrderType.MachineBreakdown, WorkOrderType.MachineProblem];

export const isAcuteWorkOrder = (workOrder) => acuteTypes.includes(workOrder?.workOrderType);

/*
 * One range's share of a list, as rows: a week's, or the weeks a month view shows. The caller
 * decides where each item is placed and whether it is still to do. The range's edges and the
 * carrying work the same for orders and for projections.
 *
 * The range is from start up to, not including, end. It is current when now falls inside it,
 * and only the current range carries.
 */
const toRangeRows = (items, start, end, now, { keyOf, placedAtOf, isStillToDo, projected }) => {
  const isCurrent = start <= now && now < end;
  const rows = [];

  (items ?? []).forEach((item) => {
    const placedAt = placedAtOf(item);
    if (!placedAt) return;

    if (placedAt >= start && placedAt < end) {
      rows.push({ key: keyOf(item, placedAt), workOrder: item, placedAt, carriedOver: false, projected });
    } else if (isCurrent && placedAt < start && isStillToDo(item)) {
      rows.push({ key: keyOf(item, placedAt), workOrder: item, placedAt, carriedOver: true, projected });
    }
  });

  return rows;
};

const weekEndOf = (weekStart) => addWeeks(startOfISOWeek(weekStart), 1);

const orderRows = {
  keyOf: (workOrder) => `wo-${workOrder.id}`,
  placedAtOf: getPlacementDate,
  isStillToDo: (workOrder) => isWorkOrderOpen(workOrder.status),
  projected: false,
};

/*
 * The orders that belong to the range from start up to end, by getWeekRows' rules, with the
 * range in place of the week. The month view's range is the whole weeks it shows.
 */
export const getRangeRows = (workOrders, start, end, now = new Date()) =>
  toRangeRows(workOrders, start, end, now, orderRows);

/*
 * The orders that belong to one week, as rows of { key, workOrder, placedAt, carriedOver,
 * projected }, where projected is false.
 *
 * An order lives in the week its placement date falls in. The current week also takes every
 * order still open from an earlier one, marked carriedOver: an unfinished job from last week is
 * this week's work, and a week view that left it behind in a week nobody looks at any more would
 * hide exactly the work that is late. It stays in its own week as well, so going back still shows
 * what that week held.
 *
 * "Open" is isWorkOrderOpen's to decide, as everywhere else. A draft is nobody's queue yet and is
 * not carried.
 */
export const getWeekRows = (workOrders, weekStart, now = new Date()) =>
  getRangeRows(workOrders, startOfISOWeek(weekStart), weekEndOf(weekStart), now);

const projectedRows = {
  keyOf: (occurrence, placedAt) => `schedule-${occurrence.scheduleId}-${placedAt.getTime()}`,
  placedAtOf: (occurrence) => toDate(occurrence.dueDate),
  isStillToDo: (occurrence) => occurrence.isOverdue === true,
  projected: true,
};

// getProjectedRows, below, for the range from start up to end, as getRangeRows is getWeekRows.
export const getProjectedRangeRows = (occurrences, start, end, now = new Date()) =>
  toRangeRows(occurrences, start, end, now, projectedRows);

/*
 * The work orders the calendar schedules are still to raise in one week. The rows have the same
 * shape as getWeekRows' rows, with projected true. The occurrences are what
 * GET recurring-schedules/projected returns.
 *
 * Each occurrence goes in the row's workOrder slot. It carries the fields the sweep will copy onto
 * the order, under the order's own names. So it is grouped, split into akut and planerad, and
 * summed by the same rules as a real order. A schedule of type machine problem is akut before its
 * order exists as well as after. An occurrence has no id and no status, because only a raised
 * order has those.
 *
 * An occurrence is placed on its due day, which is the day the sweep raises it. That is also the
 * day the order's createdAt will fall on. An overdue occurrence is one that is due but not raised
 * yet, because the sweep has not run since. If it was due in an earlier week, it is carried into
 * the current week, the same as late open work. The server only returns an overdue occurrence for
 * a range that covers now, and only the current week's range does.
 *
 * No order appears in both lists. Each time the sweep raises an order, it moves the schedule's
 * next run past it, and the projection starts from that next run.
 */
export const getProjectedRows = (occurrences, weekStart, now = new Date()) =>
  getProjectedRangeRows(occurrences, startOfISOWeek(weekStart), weekEndOf(weekStart), now);

/*
 * The page's filters, applied to projected occurrences. The filters are { status, workOrderType,
 * priority, tradeId, mineOnly, userId }, and a null filter lets everything through.
 *
 * Each filter is checked against the order the occurrence will become. That order will be Open,
 * so an occurrence passes a status filter of Open and no other status: someone looking at
 * finished work is not looking for work that has not started.
 *
 * The client cannot fully answer the Mina filter. Which queue an order is in is GetMineAsync's to
 * decide, from department and trade memberships the client does not have. Only an occurrence
 * whose schedule names userId is certain to be in the user's queue, so only those pass. The rest
 * stay out of Mina until the sweep raises them. That errs on the safe side: the user's week never
 * shows someone else's work.
 */
export const filterProjectedOccurrences = (occurrences, filters = {}) => {
  const {
    status = null,
    workOrderType = null,
    priority = null,
    tradeId = null,
    mineOnly = false,
    userId = null,
  } = filters;

  if (status !== null && status !== WorkOrderStatus.Open) return [];
  if (mineOnly && userId === null) return [];

  return (occurrences ?? []).filter((occurrence) =>
    (workOrderType === null || occurrence.workOrderType === workOrderType)
    && (priority === null || occurrence.priority === priority)
    && (tradeId === null || occurrence.tradeId === tradeId)
    && (!mineOnly || occurrence.assignedResourceId === userId));
};

/*
 * Late work first, then the week in the order it happens. At the same moment, raised orders come
 * before projected ones. A projection has no id, so projections are ordered by their schedule.
 */
const compareRows = (a, b) =>
  Number(b.carriedOver) - Number(a.carriedOver)
  || a.placedAt - b.placedAt
  || Number(a.projected) - Number(b.projected)
  || (a.projected ? a.workOrder.scheduleId - b.workOrder.scheduleId : a.workOrder.id - b.workOrder.id);

/*
 * Rows as { acute, planned }, each sorted the way the week sorts a lane. For a view with no
 * groups, the month's, where groupWeekRows has nothing to group by.
 */
export const splitAcuteAndPlanned = (rows) => {
  const acute = [];
  const planned = [];
  (rows ?? []).forEach((row) => (isAcuteWorkOrder(row.workOrder) ? acute : planned).push(row));
  return { acute: acute.sort(compareRows), planned: planned.sort(compareRows) };
};

/*
 * What a set of rows is estimated to take, as { estimatedHours, withoutEstimateCount }.
 *
 * An order with no estimate is counted, not added in as nothing: 60 hours over a week where four
 * orders were never estimated is not that week's load, and the count is what says so. Zero is an
 * estimate. estimatedHours is null when not one row carries an estimate, so a week nobody has
 * estimated reads as unknown rather than as free.
 *
 * Carried-over and projected rows are summed with the rest, because they are this week's work. A
 * projected row's estimate is its schedule's, the one the sweep copies onto the order. Summed in
 * hundredths, the precision the server keeps, so three 0.1 h jobs come to 0.3 h and not to
 * 0.30000000000000004.
 */
export const getEstimateTotal = (rows) => {
  let hundredths = 0;
  let estimatedCount = 0;
  let withoutEstimateCount = 0;

  (rows ?? []).forEach((row) => {
    const hours = row.workOrder?.estimatedHours;
    if (typeof hours === "number" && Number.isFinite(hours)) {
      hundredths += Math.round(hours * 100);
      estimatedCount += 1;
    } else {
      withoutEstimateCount += 1;
    }
  });

  return {
    estimatedHours: estimatedCount > 0 ? hundredths / 100 : null,
    withoutEstimateCount,
  };
};

const groupIdOf = (workOrder, groupBy) =>
  (groupBy === WeekGroupBy.Trade ? workOrder.tradeId : workOrder.assignedDepartmentId) ?? null;

/*
 * One week's rows per department or per trade, as
 * [{ id, label, acute, planned, estimatedHours, withoutEstimateCount }], the last two as
 * getEstimateTotal gives them for the whole group.
 *
 * Orders with no department or no trade are kept in a group of their own, with id and label null,
 * placed last. That is the group the page names "no department" / "no trade". Dropping them would
 * make a week look lighter than it is.
 *
 * getLabel resolves an id to a name. One the page cannot name, such as a department removed from
 * the tree, falls back to its id rather than to the no-group.
 */
export const groupWeekRows = (rows, groupBy, getLabel) => {
  const groups = new Map();

  (rows ?? []).forEach((row) => {
    const id = groupIdOf(row.workOrder, groupBy);
    if (!groups.has(id)) {
      groups.set(id, {
        id,
        label: id === null ? null : (getLabel?.(id) ?? `#${id}`),
        acute: [],
        planned: [],
      });
    }
    const group = groups.get(id);
    (isAcuteWorkOrder(row.workOrder) ? group.acute : group.planned).push(row);
  });

  const result = [...groups.values()];
  result.forEach((group) => {
    group.acute.sort(compareRows);
    group.planned.sort(compareRows);
    Object.assign(group, getEstimateTotal([...group.acute, ...group.planned]));
  });

  return result.sort((a, b) => {
    if (a.id === null) return 1;
    if (b.id === null) return -1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base" }) || a.id - b.id;
  });
};

const DAYS_IN_WEEK = 7;

/*
 * The week's seven days, Monday to Sunday, each at local midnight. These are the week view's
 * day columns.
 */
export const getWeekDays = (weekStart) => {
  const start = startOfISOWeek(weekStart);
  return Array.from({ length: DAYS_IN_WEEK }, (_, index) => addDays(start, index));
};

/*
 * Rows by day, as { late, days }, where days holds a list for each of the given days. The days
 * are consecutive local midnights, as getWeekDays gives them, or a month view's weeks of them.
 *
 * Carried-over rows go in late, the column before the first day, because their own day is in a
 * range that has gone. Every other row goes on the day its placement date falls on. The days are
 * counted on the calendar rather than in 24-hour steps, so a Sunday evening in the week the clocks
 * change still lands on Sunday.
 *
 * The rows keep the order they come in, which the caller has already sorted. A row outside the
 * days cannot come out of getRangeRows or getProjectedRangeRows unless it is carried, and is
 * dropped.
 */
export const splitRowsByDays = (rows, days) => {
  const late = [];
  const lists = (days ?? []).map(() => []);
  const first = days?.[0];

  (rows ?? []).forEach((row) => {
    if (row.carriedOver) {
      late.push(row);
      return;
    }
    if (!first) return;
    const index = differenceInCalendarDays(row.placedAt, first);
    if (index >= 0 && index < lists.length) lists[index].push(row);
  });

  return { late, days: lists };
};

// One lane's rows by day, Monday first: splitRowsByDays over the week's seven days.
export const splitRowsByDay = (rows, weekStart) => splitRowsByDays(rows, getWeekDays(weekStart));

/*
 * One group from groupWeekRows, as the week view's columns: { late, days }. Each column is
 * { date, acute, planned, count, estimatedHours, withoutEstimateCount }, and date is null for late.
 *
 * The count and the estimate cover both lanes, because a day's header says what that day holds
 * for the group, akut and planerad together. Each row is on one day only, so the days and late
 * add up to the group's own sums.
 */
export const getDayColumns = (group, weekStart) => {
  const acute = splitRowsByDay(group?.acute, weekStart);
  const planned = splitRowsByDay(group?.planned, weekStart);

  const toColumn = (date, acuteRows, plannedRows) => ({
    date,
    acute: acuteRows,
    planned: plannedRows,
    count: acuteRows.length + plannedRows.length,
    ...getEstimateTotal([...acuteRows, ...plannedRows]),
  });

  return {
    late: toColumn(null, acute.late, planned.late),
    days: getWeekDays(weekStart).map((date, index) => toColumn(date, acute.days[index], planned.days[index])),
  };
};

/*
 * Moving planned work to another day of the week: the "Flytta till" menu on a card, and dragging
 * after it.
 *
 * Preventive work and a problem move, because both wait for a day someone gives them. A breakdown
 * does not: its day is the day the machine stopped. Neither does finished work, which is a record
 * of when it was planned rather than a plan, and which the server refuses to move.
 */
const movableTypes = [WorkOrderType.PreventiveMaintenance, WorkOrderType.MachineProblem];

export const isMovableWorkOrder = (workOrder) =>
  movableTypes.includes(workOrder?.workOrderType)
  && workOrder.status !== WorkOrderStatus.Completed
  && workOrder.status !== WorkOrderStatus.Closed;

/*
 * The days a row can be moved to: the days on screen from today on, apart from the one it is on.
 * The days are the week's in the week view and the month grid's in the month view. A carried
 * row's own day is in a range that has gone, so it is offered every day from today.
 *
 * Not into the past, which is not a plan, and not past what is on screen: a further move is a
 * date, and the form already has a date picker. Nothing for a projection, which is not an order
 * yet, or for an order that does not move.
 */
export const getMoveTargetsIn = (row, days, now = new Date()) => {
  if (!row || row.projected || !isMovableWorkOrder(row.workOrder)) return [];
  const today = startOfDay(now);
  return (days ?? []).filter((day) => !isBefore(day, today) && !isSameDay(day, row.placedAt));
};

// getMoveTargetsIn over the week on screen.
export const getMoveTargets = (row, weekStart, now = new Date()) =>
  getMoveTargetsIn(row, getWeekDays(weekStart), now);

/*
 * Where a moved order starts: on the new day at the time it was planned for, so work set for 06:00
 * still starts at 06:00, by the wall clock even across a change of clocks.
 *
 * An order with no planned start gets midnight, which says the day and no hour. The time it was
 * raised would be wrong: for a preventive order that is the hour of the nightly run.
 */
export const getMovedStart = (workOrder, day) => {
  const start = toDate(workOrder?.plannedStartDate);
  if (!start) return startOfDay(day);
  return set(day, {
    hours: start.getHours(),
    minutes: start.getMinutes(),
    seconds: start.getSeconds(),
    milliseconds: start.getMilliseconds(),
  });
};

/*
 * The start that takes a move back, from the order as it was before the move. Null when the move
 * cannot be taken back.
 *
 * An order that had a start gets it back, and the server moves its end back by the same amount.
 * One that had no start cannot get "no start" back, since the endpoint moves dates and does not
 * clear them. It gets midnight on the day it was on instead: the same day and the same card, with
 * a start it did not have before.
 *
 * An order with an end and no start cannot be taken back at all. The move gave it a start, so
 * moving that start back would take the end with it.
 */
export const getUndoStart = (workOrder) => {
  const start = toDate(workOrder?.plannedStartDate);
  if (start) return start;
  if (toDate(workOrder?.plannedEndDate)) return null;
  const placedAt = getPlacementDate(workOrder);
  return placedAt ? startOfDay(placedAt) : null;
};
